import { Video } from '@src/interfaces/video.model';
import { VideoType } from '@src/types/video.enum';
import { Component, State, Prop, Event, EventEmitter, Element, h } from '@stencil/core';
import { loadYoutubeScript } from '@utils/youtube';

@Component({
  tag: 'vs-media',
  styleUrl: 'vs-media.css',
  shadow: false,
})
export class VsMedia {
  @Element() hostElement: HTMLVsMediaElement;
  @Prop() mediaParent;
  @Prop() mobileImage;
  @Prop() mobileImageAlt;
  @Prop() desktopImage;
  @Prop() desktopImageAlt;
  @Prop() videoEnabled;
  @Prop() accessibility?: any;
  @State() isVideoPlaying;
  @Event({ bubbles: true }) videoPlaying: EventEmitter;
  @Event({ bubbles: true }) pauseAutoplay: EventEmitter;

  @Prop() videoProps: Video;

  oldHeight: number;
  nextId = 0;
  youtubePlayer: any;
  loadingComplete = false;
  videoModalId = `video${this.nextId++}`;
  youtubeDivId = `youtube${this.nextId++}`;
  inlineState = 'collapsed';

  private playVideo() {
    this.pauseAutoplay.emit("pause");
    this.videoPlaying.emit("true");
    if (this.videoProps.openInModal != 'true') {
      this.isVideoPlaying = true;
      setTimeout(() => {
        this.openInline();
      }, 1000);
    }

  }

  private stopVideo() {
    if (this.videoProps.openInModal != 'true') {
      this.closeInline();
      this.videoPlaying.emit("false");
      setTimeout(() => {
        this.isVideoPlaying = false;
      }, 1000);
      this.youtubePlayer = null;
    }
    this.pauseAutoplay.emit("play");
  }

  openInline() {
    const elementToAnimate = this.hostElement.querySelector(".vs-marquee-media-wrapper") as HTMLElement;
    this.oldHeight = elementToAnimate.clientHeight;
    elementToAnimate.style.height = "100vh"
  }

  closeInline() {
    const elementToAnimate = this.hostElement.querySelector(".vs-marquee-media-wrapper") as HTMLElement;
    elementToAnimate.style.height = this.oldHeight + 'px';
  }

  private createYoutubePlayer() {
    if (!this.youtubePlayer && this.isVideoPlaying) {
      const youtubeVideoID = this.videoProps.videoId.split('watch?v=')[1];
      this.youtubePlayer = new window['YT'].Player(this.youtubeDivId, {
        videoId: youtubeVideoID,
        playerVars: {
          autohide: 1,
          autoplay: 0
        },
        events: {
          onReady: this.onYoutubePlayerReady.bind(this)
        }
      });
    }
  }

  onYoutubePlayerReady(event: any) {
    if (this.videoProps.openInModal === 'true') {
      this.loadingComplete = true;
      event.target.playVideo();
      event.target.h.contentWindow.focus();
    } else {
      event.target.playVideo();
      event.target.h.contentWindow.focus();
    }
  }

  private onNativePlayerReady(event: any) {
    if (this.videoProps.openInModal === 'true') {
      setTimeout(() => {
        this.loadingComplete = true;
        event.target.play();
        event.target.focus();
      }, 2000);
    } else {
      event.target.play();
      event.target.focus();
    }
  }
  //LifeCycle Methods:
  componentDidLoad() {
  }

  componentWillUpdate() {
    if (this.videoProps.videoType === VideoType.YOUTUBE) {
      loadYoutubeScript();
    }
    if (this.isVideoPlaying && this.videoProps.videoType === VideoType.YOUTUBE) {
      setTimeout(() => {
        this.createYoutubePlayer();
      }, 2000);
    }
  }
  render() {
    return (
      <div class='vs-marquee-media-wrapper'>
        <div class='vs-img-wrapper vs-position-relative'>
          {this.mediaParent == "marquee" && (
            <div>
              <img
                class='vs-d-none vs-d-lg-block vs-img-fluid vs-col-12 vs-px-0'
                src={this.desktopImage}
                alt={this.desktopImageAlt}
              />
              {this.mobileImage && (
                <img
                  class='vs-d-lg-none vs-img-fluid vs-col-12 vs-px-0'
                  src={this.mobileImage}
                  alt={this.mobileImageAlt}
                />
              )}
              {this.videoEnabled && this.videoProps.videoId && (
                <button
                  class={"vs-video-play-button" + (this.videoProps.openInModal === 'true' ? ' modal__show' : '') + (this.videoProps.buttonStyle == 'white' ? ' vs-alt' : '')}
                  onClick={() => this.playVideo()}
                  aria-label={`${this?.accessibility?.playButtonAriaLabel || ""} ${this.videoProps.videoTitle}`}
                  data-title={this.videoProps.videoTitle}
                  data-toggle={this.videoProps.openInModal ? "modal" : ""}
                  data-target={this.videoProps.openInModal === 'true' ? 'cta-modal-target' : this.videoProps.videoId}
                  data-modaltype="video"
                  data-videoid={this.videoProps.openInModal === 'true' ? this.videoProps.videoId?.split('watch?v=')[1] : this.videoProps.videoId}>
                </button>
              )}
            </div>
          )}
          {this.mediaParent == "submarquee" && [
            <img class="vs-d-none vs-d-lg-block" src={this.desktopImage} alt={this.desktopImageAlt} />,
            <img class="vs-d-lg-none vs-img-fluid" src={this.desktopImage} alt={this.desktopImageAlt} />
          ]}
          {this.videoEnabled && this.videoProps.videoId && (
            <button
              class={"vs-video-play-button" + (this.videoProps.openInModal === 'true' ? ' modal__show' : '') + (this.videoProps.buttonStyle == 'white' ? ' vs-alt' : '')}
              onClick={() => this.playVideo()}
              aria-label={`${this?.accessibility?.playButtonAriaLabel || ""} ${this.videoProps.videoTitle}`}
              data-title={this.videoProps.videoTitle}
              data-toggle={this.videoProps.openInModal ? "modal" : ""}
              data-target={this.videoProps.openInModal === 'true' ? 'cta-modal-target' : this.videoProps.videoId}
              data-modaltype="video"
              data-component={this.mediaParent}
              data-videoid={this.videoProps.openInModal === 'true' ? this.videoProps.videoId?.split('watch?v=')[1] : this.videoProps.videoId}>
            </button>
          )}
        </div>
        {/* Inline Video */}
        {
          !(this.videoProps.openInModal === 'true') && this.isVideoPlaying && (
            <div class="vs-video-inline vs-fade-in" style={{ overflow: "contain" }}>
              <div class="vs-container-fluid">
                <div class="vs-row">
                  <h2 class="vs-text vs-alt vs-col-11 vs-video-title">{this.videoProps.videoTitle}</h2>
                  <button class="vs-col-1 vs-video-close-button" aria-label="Close Video" onClick={() => this.stopVideo()}></button>
                </div>
              </div>
              <div style={{ height: "94%" }}>
                {this.videoProps.openInModal === 'true' && !this.loadingComplete && (this.videoProps.videoType != VideoType.YOUKU) && (this.videoProps.videoType != VideoType.VBRICK_VISATV) && (
                  <div class="vs-d-lg-none vs-height-full">
                    <img src={this.mobileImage} alt={this.mobileImageAlt} width="100%" height="auto" />
                  </div>
                )}
                {this.videoProps?.openInModal === 'true' && !this.loadingComplete && (this.videoProps.videoType != VideoType.YOUKU) && (this.videoProps.videoType != VideoType.VBRICK_VISATV) && (
                  <img class="vs-d-none vs-d-lg-block" src={this.desktopImage} alt={this.desktopImageAlt} width="100%" height="auto" />
                )}
                {this.videoProps.videoType === VideoType.YOUTUBE && (
                  <div id={this.youtubeDivId}></div>
                )}
                {this.videoProps.videoType === VideoType.INTERNAL && (
                  <video onCanPlay={(event) => this.onNativePlayerReady(event)} controls>
                    <source src={this.videoProps.videoId} />
                  </video>
                )}
              </div>
            </div>
          )
        }

      </div >
    );
  }

}
